/**
 * AI Guide — The Heart of YAS Connect
 * 
 * This is not a career tool. This is a personal mentor.
 * It knows everything about the participant — interests, skills, goals,
 * activities, journal entries, career explorations, wins — and uses ALL
 * of it to provide guidance, direction, and support.
 * 
 * For youth who feel lost, unsure, unmotivated, or directionless —
 * this is the place that meets them where they are and shows them
 * where they could go.
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, Send, ArrowLeft, Heart, Target, Briefcase, Zap,
  Star, Award, Sparkles, Loader2, ChevronRight, TrendingUp,
  Lightbulb, MessageCircle, CheckCircle, Users, GraduationCap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import VoiceInput from '@/components/VoiceInput';
import Navigation from '@/components/Navigation';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { formatDisplayName } from '@/utils/formatText';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const AIGuidePage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(`guide_${user?.id}_${Date.now()}`);
  const [activeView, setActiveView] = useState('overview'); // overview | chat
  const chatEndRef = useRef(null);
  const [hasGreeted, setHasGreeted] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProfile(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Auto-greet when entering chat for the first time
  useEffect(() => {
    if (activeView === 'chat' && !hasGreeted && user) {
      setHasGreeted(true);
      const interests = user.interests || profile?.interests || [];
      const formattedInterests = interests.slice(0, 3).map(i => formatDisplayName(i)).join(', ');
      const greeting = `Hey ${user.name?.split(' ')[0] || 'there'}! I'm your AI career coach. I know a bit about you already — ${interests.length > 0 ? `you're interested in ${formattedInterests}` : "let's figure out your interests together"}. I'm here to help you explore careers, set goals, and figure out your next steps. What's on your mind?`;
      setMessages([{ role: 'assistant', content: greeting }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/api/ai/my-profile-summary`, { headers: hdr() });
      if (res.ok) setProfile(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    const userMsg = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);
    try {
      const res = await fetch(`${API}/api/ai/chat`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ message: userMsg, session_id: sessionId })
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch (e) { toast.error('Connection issue — try again'); }
    setChatLoading(false);
  };

  const askQuickQuestion = (q) => {
    setActiveView('chat');
    setInput(q);
    setTimeout(() => {
      setInput(q);
      sendMessageDirect(q);
    }, 100);
  };

  const sendMessageDirect = async (msg) => {
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatLoading(true);
    try {
      const res = await fetch(`${API}/api/ai/chat`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ message: msg, session_id: sessionId })
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch (e) { toast.error('Connection issue'); }
    setChatLoading(false);
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3" style={{ backgroundColor: '#F5F3EF' }}>
      <Brain className="w-12 h-12 text-orange-500 animate-pulse" />
      <p className="text-sm text-gray-500">Loading your AI Guide...</p>
    </div>
  );

  const name = profile?.user_name?.split(' ')[0] || 'there';
  const id = profile?.identity_strength || {};
  const strengthCount = Object.values(id).filter(Boolean).length;
  const strengthPct = Math.round((strengthCount / 6) * 100);

  // Generate contextual quick questions based on profile
  const quickQuestions = [];
  if (!id.interests) quickQuestions.push("Help me figure out what I'm interested in");
  if (!id.careers) quickQuestions.push("What careers would be good for someone like me?");
  if (!id.goals) quickQuestions.push("I don't know what goals to set — can you help?");
  if (id.careers && profile?.career_explorations?.length > 0) quickQuestions.push(`Tell me more about being a ${profile.career_explorations[0]?.title}`);
  if (id.interests) quickQuestions.push("Based on my interests, what should I be doing right now?");
  quickQuestions.push("I feel lost and don't know what I want to do with my life");
  quickQuestions.push("How do I figure out what I'm good at?");
  if (profile?.program_type === 'syep') quickQuestions.push("How can I make the most of my summer job?");
  if (profile?.program_type === 'student_pathways_gps') quickQuestions.push("How do I start planning for after high school?");

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="px-4 py-3 flex items-center gap-3 shadow-sm" style={{ backgroundColor: '#2D2824' }}>
        <button onClick={() => { if (activeView === 'chat') { setActiveView('overview'); } else { navigate(-1); } }} className="p-2 rounded-lg hover:bg-white/20" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <p style={{ color: 'white' }} className="font-bold text-sm">Your AI Guide</p>
          <p style={{ color: 'rgba(255,255,255,0.6)' }} className="text-[10px]">Personal guidance based on everything you've shared</p>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setActiveView('overview')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activeView === 'overview' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-300'}`}>
            My Breakdown
          </button>
          <button onClick={() => setActiveView('chat')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activeView === 'chat' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-300'}`}>
            Chat
          </button>
        </div>
      </div>

      {activeView === 'overview' ? (
        /* ==================== OVERVIEW: Profile Breakdown ==================== */
        <div className="flex-1 overflow-y-auto pb-6">
          <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
            {/* Identity Strength */}
            <Card className="p-5 rounded-2xl bg-white shadow-sm">
              <h2 className="font-bold text-base text-gray-900 mb-1">Your Identity Map</h2>
              <p className="text-xs text-gray-500 mb-4">The more you share, the better I can guide you</p>
              <Progress value={strengthPct} className="h-3 mb-2" />
              <p className="text-xs text-gray-500 mb-4">{strengthCount}/6 areas built — {strengthPct < 50 ? "let's fill in the gaps" : strengthPct < 80 ? "getting strong!" : "your profile is powerful"}</p>

              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'interests', label: 'Interests', icon: Heart, color: '#EC4899', count: profile?.interests?.length || 0, route: '/interests' },
                  { key: 'skills', label: 'Skills', icon: Zap, color: '#F59E0B', count: profile?.skills?.length || 0, route: '/skills' },
                  { key: 'goals', label: 'Goals', icon: Target, color: '#10B981', count: (profile?.active_goals?.length || 0) + (profile?.completed_goals?.length || 0), route: '/goals' },
                  { key: 'careers', label: 'Careers', icon: Briefcase, color: '#8B5CF6', count: profile?.career_explorations?.length || 0, route: '/ai-counselor' },
                  { key: 'activities', label: 'Activities', icon: Users, color: '#3B82F6', count: profile?.activities?.length || 0, route: '/interests' },
                  { key: 'portfolio', label: 'Portfolio', icon: Award, color: '#F97316', count: profile?.wins_count || 0, route: '/vault' },
                ].map(item => (
                  <button key={item.key} onClick={() => navigate(item.route)}
                    className={`flex items-center gap-3 p-3 rounded-xl text-left transition-all hover:shadow-sm ${id[item.key] ? 'bg-gray-50' : 'bg-gray-50 border-2 border-dashed border-gray-200'}`}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: id[item.key] ? `${item.color}15` : '#F3F4F6' }}>
                      <item.icon className="w-4 h-4" style={{ color: id[item.key] ? item.color : '#9CA3AF' }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-800">{item.label}</p>
                      <p className="text-[10px] text-gray-500">{item.count > 0 ? `${item.count} added` : 'Not started'}</p>
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            {/* What I Know About You */}
            {(profile?.interests?.length > 0 || profile?.career_explorations?.length > 0 || profile?.active_goals?.length > 0) && (
              <Card className="p-5 rounded-2xl bg-white shadow-sm">
                <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-orange-500" /> What I Know About You
                </h3>
                {profile?.interests?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Interests</p>
                    <div className="flex flex-wrap gap-1">{profile.interests.map((i, idx) => (
                      <Badge key={idx} className="text-xs bg-pink-50 text-pink-700 border border-pink-200">{formatDisplayName(i)}</Badge>
                    ))}</div>
                  </div>
                )}
                {profile?.skills?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Skills</p>
                    <div className="flex flex-wrap gap-1">{profile.skills.map((s, idx) => (
                      <Badge key={idx} className="text-xs bg-amber-50 text-amber-700 border border-amber-200">{formatDisplayName(s)}</Badge>
                    ))}</div>
                  </div>
                )}
                {profile?.career_explorations?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Careers You're Exploring</p>
                    {profile.career_explorations.slice(0, 3).map((c, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-orange-50 mb-1">
                        <Briefcase className="w-3.5 h-3.5 text-orange-500" />
                        <span className="text-xs font-semibold text-orange-800">{c.title}</span>
                        <span className="text-[10px] text-gray-500 ml-auto">{c.salary}</span>
                      </div>
                    ))}
                  </div>
                )}
                {profile?.active_goals?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Active Goals</p>
                    {profile.active_goals.slice(0, 3).map((g, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 mb-1">
                        <Target className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="text-xs font-semibold text-emerald-800">{g.title}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Quick Questions — Tap to Chat */}
            <Card className="p-5 rounded-2xl bg-white shadow-sm">
              <h3 className="font-bold text-sm text-gray-900 mb-1">Ask Me Anything</h3>
              <p className="text-xs text-gray-500 mb-3">Tap a question or switch to Chat to talk freely</p>
              <div className="space-y-2">
                {quickQuestions.slice(0, 6).map((q, i) => (
                  <button key={i} onClick={() => askQuickQuestion(q)}
                    className="flex items-center gap-3 w-full p-3 rounded-xl bg-orange-50 hover:bg-orange-100 text-left transition-all"
                    data-testid={`quick-q-${i}`}>
                    <MessageCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    <span className="text-sm text-orange-900">{q}</span>
                    <ChevronRight className="w-4 h-4 text-orange-400 ml-auto flex-shrink-0" />
                  </button>
                ))}
              </div>
            </Card>
          </div>
        </div>
      ) : (
        /* ==================== CHAT VIEW ==================== */
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="max-w-2xl mx-auto space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <Brain className="w-16 h-16 mx-auto mb-4 text-orange-200" />
                  <p className="text-lg font-bold text-gray-900 mb-2">I'm here for you, {name}.</p>
                  <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                    Ask me anything — about careers, goals, what you're good at, what to do next, 
                    or just talk about how you're feeling. I know your full profile and I'm here to help.
                  </p>
                  <div className="space-y-2 max-w-sm mx-auto">
                    {quickQuestions.slice(0, 4).map((q, i) => (
                      <button key={i} onClick={() => sendMessageDirect(q)}
                        className="flex items-center gap-2 w-full p-3 rounded-xl bg-orange-50 hover:bg-orange-100 text-left transition-all">
                        <MessageCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        <span className="text-sm text-orange-800">{q}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((msg, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-4 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-orange-600 text-white rounded-br-sm' 
                      : 'bg-white shadow-sm border border-gray-100 text-gray-800 rounded-bl-sm'
                  }`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <Brain className="w-3.5 h-3.5 text-orange-500" />
                        <span className="text-[10px] font-bold text-orange-600">AI Guide</span>
                      </div>
                    )}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </motion.div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white shadow-sm border border-gray-100 rounded-2xl rounded-bl-sm p-4 max-w-[85%]">
                    <div className="flex items-center gap-2">
                      <Brain className="w-3.5 h-3.5 text-orange-500" />
                      <div className="flex gap-1"><div className="w-2 h-2 bg-violet-300 rounded-full animate-bounce" /><div className="w-2 h-2 bg-violet-300 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} /><div className="w-2 h-2 bg-violet-300 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} /></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Chat Input */}
          <div className="px-4 py-3 border-t border-gray-100 bg-white">
            <div className="max-w-2xl mx-auto flex gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Ask me anything..."
                className="flex-1 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-orange-300"
                data-testid="chat-input"
              />
              <VoiceInput size="md" onTranscript={(text) => { setInput(prev => prev ? prev + ' ' + text : text); }} />
              <Button onClick={sendMessage} disabled={chatLoading || !input.trim()}
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl px-4" data-testid="send-btn">
                {chatLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      )}
      <Navigation role="youth" />
    </div>
  );
};

export default AIGuidePage;
